import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button, LoadingIndicator, Typography } from 'lib/components';
import { JobStatus } from 'lib/util';
import parseTreatmentType from 'lib/util/parseTreatmentType';
import { useAddIdPrefix, useComprehensiveTopNav, useCustomToast, useInvoiceDownload } from 'hooks';
import {
	actions,
	RequestManager,
	useAppDispatch,
	useSelectAuthorization,
	useSelectInvoice,
	useSelectJob,
	useSelectRequest,
	useSelectSettings,
} from 'store';
import routes from 'lib/api/routes';
import { InvoicePaymentStatusType, InvoiceStatusType } from 'lib/util';
import { Invoice, InvoiceItemType } from 'types';
import adjustTime from '../../lib/adjustTime';
import {
	BillingReviewStatus,
	billingReviewStatusLabel,
	clearBillingReviewOverride,
	getBillingReviewStatus,
	setBillingReviewOverride,
} from '../BillingScreen/reviewStatus';
import classes from './index.module.scss';

const REVIEW_BADGE_CLASS: Record<BillingReviewStatus, string> = {
	pending: classes['review-badge-pending'],
	needs_fix: classes['review-badge-needs_fix'],
	approved: classes['review-badge-approved'],
	sent: classes['review-badge-sent'],
	voided: classes['review-badge-voided'],
};

const BillingReviewScreen = () => {
	const { id } = useParams<{ id: string }>();
	const dispatch = useAppDispatch();
	const { active } = useSelectRequest();
	const getInvoiceById = useSelectInvoice();
	const [invoice, setInvoice] = useState<Invoice>();

	useEffect(() => {
		dispatch(actions.fetchInvoice(parseInt(id, 10)));
	}, [dispatch, id]);

	useEffect(() => {
		const RM = new RequestManager(active);
		if (RM.idle()) return;

		if (RM.req(routes.INVOICE_ID(parseInt(id, 10)), 'GET') && RM.succeeded()) {
			const next = getInvoiceById(parseInt(id, 10));
			if (next) setInvoice(next);
		}
	}, [active, getInvoiceById, id]);

	if (!invoice) return <LoadingIndicator />;
	return <BillingReviewView key={invoice.id} invoice={invoice} />;
};

const BillingReviewView = ({ invoice }: { invoice: Invoice }) => {
	const { push } = useHistory();
	const toast = useCustomToast();
	const addPrefix = useAddIdPrefix();
	const dispatch = useAppDispatch();
	const settingsState = useSelectSettings();
	const getJobById = useSelectJob();
	const { canVoidInvoice } = useSelectAuthorization();
	const { active } = useSelectRequest();

	const [comment, setComment] = useState('');
	const [statusTick, setStatusTick] = useState(0);
	const [jobLoading, setJobLoading] = useState(false);
	const [regenerating, setRegenerating] = useState(false);
	const [pdfBusy, setPdfBusy] = useState(false);

	const reviewStatus = useMemo(() => getBillingReviewStatus(invoice), [invoice, statusTick]);
	const canRegenerate =
		canVoidInvoice &&
		invoice.jobId != null &&
		invoice.status === InvoiceStatusType.CREATED &&
		invoice.paymentStatus !== InvoicePaymentStatusType.PAID;

	useComprehensiveTopNav(`Billing · ${addPrefix(invoice.id)}`);
	useInvoiceDownload();

	useEffect(() => {
		if (invoice.jobId == null) return;
		setJobLoading(true);
		dispatch(actions.fetchJob(invoice.jobId));
	}, [dispatch, invoice.jobId]);

	useEffect(() => {
		if (invoice.jobId == null) {
			setJobLoading(false);
			return;
		}
		const RM = new RequestManager(active);
		if (RM.idle()) return;
		if (RM.req(routes.JOB_ID(invoice.jobId), 'GET')) {
			if (RM.succeeded() || RM.failed()) setJobLoading(false);
		}
	}, [active, invoice.jobId]);

	const refreshStatus = () => setStatusTick((n) => n + 1);

	const mark = (status: 'pending' | 'needs_fix' | 'approved' | 'sent') => {
		if (status === 'needs_fix' && !comment.trim()) {
			toast('Add a comment explaining what needs to be fixed.', 'error');
			return;
		}
		setBillingReviewOverride(invoice.id, status);
		refreshStatus();
		toast(
			status === 'approved'
				? 'Marked approved (local test state)'
				: status === 'needs_fix'
				? 'Marked needs fix (local test state)'
				: status === 'sent'
				? 'Marked sent (local test state)'
				: 'Reset to needs review'
		);
	};

	useEffect(() => {
		const RM = new RequestManager(active);
		if (RM.idle()) return;

		if (RM.req(routes.INVOICE_REGENERATE(invoice.id), 'POST')) {
			if (RM.failed()) {
				setRegenerating(false);
				toast(active.message || 'Regenerate failed', 'error');
			} else if (RM.succeeded()) {
				setRegenerating(false);
				const newId = (active.payload as { invoiceId?: number } | undefined)?.invoiceId;
				clearBillingReviewOverride(invoice.id);
				toast('Invoice regenerated from job');
				if (newId) {
					setBillingReviewOverride(newId, 'pending');
					push(`/billing/review/${newId}`);
				} else {
					push('/billing');
				}
			}
		}

		if (RM.req(routes.INVOICE_GENERATE_ID(invoice.id), 'GET')) {
			if (RM.failed()) {
				setPdfBusy(false);
				toast(active.message || 'PDF generation failed', 'error');
			} else if (RM.succeeded()) {
				setPdfBusy(false);
				toast('PDF generated');
				dispatch(actions.fetchInvoice(invoice.id));
			}
		}

		if (RM.req(routes.INVOICE_SEND_ID(invoice.id), 'GET')) {
			if (RM.failed()) {
				setPdfBusy(false);
				toast(active.message || 'Failed to email PDF', 'error');
			} else if (RM.succeeded()) {
				setPdfBusy(false);
				toast('Invoice PDF emailed to hospital');
				setBillingReviewOverride(invoice.id, 'sent');
				refreshStatus();
				dispatch(actions.fetchInvoice(invoice.id));
			}
		}
	}, [active, dispatch, invoice.id, push, toast]);

	const job = invoice.jobId != null ? getJobById(invoice.jobId) : undefined;

	const warnings = useMemo(() => {
		const list: string[] = [];
		if (invoice.jobId != null && job && (!job.services || job.services.length === 0)) {
			list.push('Job has no services — invoice may be missing dialysis charges.');
		}
		if (invoice.total === 0) {
			list.push('Invoice total is $0.');
		}
		if (invoice.items?.some((item) => item.isVoided)) {
			list.push('Some line items are voided. Confirm totals still look right.');
		}
		if (!invoice.path) {
			list.push('No PDF generated yet.');
		}
		if (invoice.paymentStatus === InvoicePaymentStatusType.PAID) {
			list.push('Paid invoices cannot be regenerated. Use credit note flow later.');
		}
		return list;
	}, [invoice, job]);

	const handleRegenerate = () => {
		if (!canRegenerate || regenerating) return;
		const ok = window.confirm(
			'This voids the current unpaid invoice and creates a new one from the job’s current services, inventory, and costs. Continue?'
		);
		if (!ok) return;
		setRegenerating(true);
		dispatch(actions.regenerateInvoice(invoice.id, comment.trim() || undefined));
	};

	const handleGeneratePdf = () => {
		if (pdfBusy) return;
		setPdfBusy(true);
		dispatch(actions.generatePDF(invoice.id));
	};

	const handleSendPdf = () => {
		if (pdfBusy) return;
		setPdfBusy(true);
		dispatch(actions.sendPDF(invoice.id));
	};

	const lineItems = (invoice.items ?? []).filter((item) => item.typeId !== InvoiceItemType.CUSTOM_COST);
	const customCosts = (invoice.items ?? []).filter((item) => item.typeId === InvoiceItemType.CUSTOM_COST);

	return (
		<div className={classes['review']}>
			<div className={classes['review-header']}>
				<div className={classes['review-headerMeta']}>
					<Typography variant="h4" weight="medium">
						{addPrefix(invoice.id)}
						{invoice.hospital?.name ? ` · ${invoice.hospital.name}` : ''}
					</Typography>
					<Typography variant="h6" style={{ opacity: 0.7 }}>
						Created {adjustTime(settingsState, invoice.createdAt).format('MM/DD/YYYY hh:mm A')}
					</Typography>
					<span className={`${classes['review-badge']} ${REVIEW_BADGE_CLASS[reviewStatus]}`}>
						{billingReviewStatusLabel(reviewStatus)}
					</span>
				</div>
				<div className={classes['review-headerActions']}>
					<Button title="Back to queue" variant="clear" color="gray" onClick={() => push('/billing')} />
					<Button
						title={pdfBusy ? 'Working…' : 'Generate PDF'}
						variant="standard"
						disabled={pdfBusy || regenerating}
						onClick={handleGeneratePdf}
					/>
					<Button
						title="Email PDF"
						variant="standard"
						color="success"
						disabled={pdfBusy || regenerating}
						onClick={handleSendPdf}
					/>
					{canRegenerate && (
						<Button
							title={regenerating ? 'Regenerating…' : 'Regenerate from job'}
							variant="standard"
							color="danger"
							disabled={regenerating || pdfBusy}
							onClick={handleRegenerate}
						/>
					)}
					<Button title="Needs fix" variant="standard" color="warning" onClick={() => mark('needs_fix')} />
					<Button title="Approve" variant="standard" color="success" onClick={() => mark('approved')} />
					<Button title="Approve & mark sent" variant="standard" onClick={() => mark('sent')} />
				</div>
			</div>

			{warnings.length > 0 && (
				<div className={`${classes['review-banner']} ${classes['review-banner-warn']}`}>
					<Typography variant="h6" weight="medium">
						Warnings
					</Typography>
					<ul className={classes['review-list']}>
						{warnings.map((w) => (
							<li key={w}>
								<Typography variant="h6">{w}</Typography>
							</li>
						))}
					</ul>
				</div>
			)}

			<div className={classes['review-banner']}>
				<Typography variant="h6">
					This screen is the invoice you’re reviewing (lines + totals) plus job context. Generate PDF / Email
					PDF use the same server flow as classic Invoices. “Regenerate from job” voids and rebuilds the bill
					from current job data. Approve / Needs fix are local review labels only.
				</Typography>
			</div>

			<div className={classes['review-panes']}>
				<div className={classes['review-pane']}>
					<div className={classes['review-paneTitle']}>
						<Typography variant="h5" weight="bold">
							Job source
						</Typography>
					</div>

					{invoice.jobId == null ? (
						<>
							<div className={classes['review-field']}>
								<Typography className={classes['review-field-label']} variant="h6">
									Type
								</Typography>
								<Typography variant="h6">Accrued / range invoice</Typography>
							</div>
							<div className={classes['review-field']}>
								<Typography className={classes['review-field-label']} variant="h6">
									Range
								</Typography>
								<Typography variant="h6">
									{invoice.rangeFrom && invoice.rangeTo
										? `${invoice.rangeFrom} – ${invoice.rangeTo}`
										: '—'}
								</Typography>
							</div>
						</>
					) : jobLoading && !job ? (
						<LoadingIndicator />
					) : job ? (
						<>
							<div className={classes['review-field']}>
								<Typography className={classes['review-field-label']} variant="h6">
									Job
								</Typography>
								<Typography variant="h6">
									{addPrefix(job.id)} · {JobStatus.text(job.status)}
								</Typography>
							</div>
							<div className={classes['review-field']}>
								<Typography className={classes['review-field-label']} variant="h6">
									Treatment
								</Typography>
								<Typography variant="h6">{parseTreatmentType(job.treatmentType)}</Typography>
							</div>
							<div className={classes['review-field']}>
								<Typography className={classes['review-field-label']} variant="h6">
									Nurse
								</Typography>
								<Typography variant="h6">{job.nurse?.user.name ?? '—'}</Typography>
							</div>
							<div className={classes['review-field']}>
								<Typography className={classes['review-field-label']} variant="h6">
									Services on job
								</Typography>
								{job.services?.length ? (
									<ul className={classes['review-list']}>
										{job.services.map((s) => (
											<li key={s.serviceId}>
												<Typography variant="h6">{s.name}</Typography>
											</li>
										))}
									</ul>
								) : (
									<Typography variant="h6" style={{ color: '#a94442' }}>
										None
									</Typography>
								)}
							</div>
							{job.inventory?.length > 0 && (
								<div className={classes['review-field']}>
									<Typography className={classes['review-field-label']} variant="h6">
										Inventory
									</Typography>
									<ul className={classes['review-list']}>
										{job.inventory.map((item) => (
											<li key={item.inventoryId}>
												<Typography variant="h6">
													{item.name} × {item.amount}
												</Typography>
											</li>
										))}
									</ul>
								</div>
							)}
							<Button
								title="Open full job"
								variant="clear"
								onClick={() => push(`/jobs/view/${job.id}`)}
							/>
						</>
					) : (
						<Typography variant="h6">Could not load job {addPrefix(invoice.jobId)}.</Typography>
					)}
				</div>

				<div className={classes['review-pane']}>
					<div className={classes['review-paneTitle']}>
						<Typography variant="h5" weight="bold">
							Bill
						</Typography>
					</div>

					<table className={classes['review-table']}>
						<thead>
							<tr>
								<th>Item</th>
								<th>Qty</th>
								<th>Price</th>
								<th>Total</th>
							</tr>
						</thead>
						<tbody>
							{lineItems.map((item) => (
								<tr key={item.id} className={item.isVoided ? classes['voided'] : undefined}>
									<td>{item.name}</td>
									<td>{item.amount}</td>
									<td>${item.price.toFixed(2)}</td>
									<td>${item.total.toFixed(2)}</td>
								</tr>
							))}
							{lineItems.length === 0 && (
								<tr>
									<td colSpan={4}>No line items</td>
								</tr>
							)}
						</tbody>
					</table>

					<div className={classes['review-totals']}>
						{customCosts.map((item) => (
							<div key={item.id} className={classes['review-totalRow']}>
								<Typography variant="h6">{item.name}</Typography>
								<Typography variant="h6">${item.total.toFixed(2)}</Typography>
							</div>
						))}
						<div className={classes['review-totalRow']}>
							<Typography variant="h6">Subtotal</Typography>
							<Typography variant="h6">${invoice.subtotal.toFixed(2)}</Typography>
						</div>
						<div className={classes['review-totalRow']}>
							<Typography variant="h6">Tax ({invoice.taxPercentage}%)</Typography>
							<Typography variant="h6">${invoice.tax.toFixed(2)}</Typography>
						</div>
						<div className={classes['review-totalRow']}>
							<Typography variant="h5" weight="bold">
								Total
							</Typography>
							<Typography variant="h5" weight="bold">
								${invoice.total.toFixed(2)}
							</Typography>
						</div>
					</div>

					<div className={classes['review-comment']}>
						<Typography variant="h6" weight="medium">
							Review comment (required for Needs fix)
						</Typography>
						<textarea
							value={comment}
							onChange={(e) => setComment(e.target.value)}
							placeholder="e.g. Missing HD service on job — regenerate after fix"
						/>
					</div>
				</div>
			</div>

			<div className={classes['review-footer']}>
				<Button
					title="Open classic invoice detail"
					variant="clear"
					color="gray"
					onClick={() => push(`/invoices/view/${invoice.id}`)}
				/>
				<Button
					title={pdfBusy ? 'Working…' : 'Generate PDF'}
					variant="standard"
					disabled={pdfBusy || regenerating}
					onClick={handleGeneratePdf}
				/>
				{invoice.path && (
					<Button
						title="Open stored PDF"
						variant="standard"
						onClick={() => window.open(invoice.path, '_blank')}
					/>
				)}
				<Button title="Reset to needs review" variant="clear" onClick={() => mark('pending')} />
			</div>
		</div>
	);
};

export default BillingReviewScreen;
